import { RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { Brand } from '@/components/Brand';
import { type BuildInfo, builtAt, commitUrl, shortCommit } from '@/lib/build';
import { cn } from '@/lib/utils';
import { MODULES } from '@/modules';

/** A module's unread badge: a count, magenta when it includes a mention. */
export interface ModuleBadge {
  count: number;
  mention: boolean;
}

/** `children` go under the modules (the game switcher). */
export function Sidebar({
  version,
  environment,
  build,
  badges = {},
  className,
  children,
}: {
  version: string | undefined;
  className?: string;
  /** Which Supabase project this build uses; dev shows a chip so test data isn't mistaken for real. */
  environment?: 'dev' | 'prod';
  /** The commit this copy was built from, shown next to the version. */
  build?: BuildInfo | null;
  badges?: Partial<Record<string, ModuleBadge>>;
  children?: ReactNode;
}) {
  return (
    <aside
      className={cn(
        'relative flex min-h-0 flex-col border-r border-line bg-[linear-gradient(180deg,var(--color-bg-2),var(--color-bg)_70%)] before:absolute before:top-0 before:-right-px before:h-full before:w-px before:bg-[linear-gradient(180deg,var(--color-cyan),transparent_30%,transparent_70%,var(--color-magenta))] before:opacity-70',
        className,
      )}
    >
      <NavLink to="/" className="mb-3 border-b border-line px-4 pt-[18px] pb-4" aria-label="Studio home">
        <Brand />
      </NavLink>
      <p className="hud-label px-4 pb-1.5">Modules</p>
      <div className="flex-1 overflow-y-auto">
        <nav className="px-2.5 pb-2.5" aria-label="Modules">
          {MODULES.map(({ id, name, path, summary, icon: Icon }) => (
            <NavLink
              key={id}
              to={path}
              className={({ isActive }) =>
                cn(
                  'cut-tr mb-1 flex items-center gap-3 px-3 py-2 text-text shadow-[inset_0_0_0_1px_transparent] transition-colors hover:bg-cyan/5 hover:shadow-[inset_0_0_0_1px_var(--color-line-hot)]',
                  isActive &&
                    'bg-[linear-gradient(90deg,rgb(0_240_255/0.14),rgb(0_240_255/0.02))] shadow-[inset_2px_0_0_var(--color-cyan),inset_0_0_0_1px_rgb(0_240_255/0.3)] [&_.module-name]:text-cyan [&_.module-name]:text-shadow-[0_0_8px_rgb(0_240_255/0.5)]',
                )
              }
            >
              <Icon className="size-4 shrink-0 text-magenta" aria-hidden />
              <span className="min-w-0">
                <span className="module-name block font-display text-xs font-bold tracking-[0.14em] uppercase">
                  {name}
                </span>
                <span className="block truncate text-[13px] text-muted">{summary}</span>
              </span>
              {badges[id]?.count ? (
                <span
                  aria-label={`${badges[id].count} unread${badges[id].mention ? ', with mentions' : ''}`}
                  className={cn(
                    'ml-auto min-w-5 px-1 text-center font-mono text-[10px] text-ink',
                    badges[id].mention ? 'bg-magenta' : 'bg-cyan',
                  )}
                >
                  {badges[id].count > 99 ? '99+' : badges[id].count}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        {children}
      </div>
      <div className="flex items-center gap-2 border-t border-line px-4 pt-2.5 pb-3 font-mono text-[10px] tracking-[0.12em] text-green uppercase">
        <span className="size-[7px] animate-pulse-dot rounded-full bg-green shadow-glow-green" aria-hidden />
        Uplink
        {environment === 'dev' ? (
          <span
            title="Connected to hive-dev, the test database"
            className="bg-yellow px-1.5 py-px font-bold tracking-[0.18em] text-ink"
          >
            Dev
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            aria-label="Reload Hive"
            title="Reload Hive"
            className="cursor-pointer text-dim hover:text-cyan focus-visible:outline-2 focus-visible:outline-yellow"
          >
            <RefreshCw className="size-3" aria-hidden />
          </button>
          <BuildStamp version={version} build={build} />
        </span>
      </div>
    </aside>
  );
}

/** "web · 0f0affb" (or "v0.1.0 · 0f0affb" on desktop), linking to the commit it was built from. */
function BuildStamp({ version, build }: { version: string | undefined; build: BuildInfo | null | undefined }) {
  const name = version ? `v${version}` : 'web';
  if (!build || build.commit === 'unknown') return <span className="text-dim">{name}</span>;
  const when = builtAt(build.time);
  return (
    <a
      href={commitUrl(build.commit)}
      target="_blank"
      rel="noreferrer"
      title={`Built ${when} from commit ${shortCommit(build.commit)}`}
      className="text-dim hover:text-cyan"
    >
      {name} · <span className="normal-case">{shortCommit(build.commit)}</span>
    </a>
  );
}
